export interface UserSpace {
  id: string;
  username: string;
  displayName: string;
  role: "admin" | "user";
  status: "active" | "disabled";
  groupIds: string[];
  sourceIds: string[];
  createdAt: string;
  updatedAt: string;
}

export interface UserGroup {
  id: string;
  name: string;
  sourceIds: string[];
  createdAt: string;
  updatedAt: string;
}

export interface SourceManifest {
  id: string;
  name: string;
  version: string;
  kinds: string[];
  capabilities: string[];
}

export type ContentKind = "novel" | "comic" | "news";

export interface SourceStatus {
  sourceId: string;
  healthy: boolean;
  message: string;
  checkedAt: string;
  latencyMs?: number;
  loginExpired?: boolean;
}

export interface UserSourceGroup {
  id: string;
  userId: string;
  name: string;
  sourceIds: string[];
  builtIn?: boolean;
  updatedAt: string;
}

export interface SourceDiscoverItem {
  title: string;
  author?: string;
  category?: string;
  description?: string;
  sourceBookId?: string;
  sourceUrl?: string;
  coverUrl?: string;
}

export interface SourceDiscoverSection {
  id: string;
  title: string;
  layout: "nav" | "featured" | "list";
  items: SourceDiscoverItem[];
}

export interface SourceDiscoverResult {
  sourceId: string;
  refreshedAt: string;
  title?: string;
  currentUrl?: string;
  pagination?: {
    currentPage?: number;
    totalPages?: number;
    previousUrl?: string;
    nextUrl?: string;
  };
  sections: SourceDiscoverSection[];
}

export interface TelemetryEvent {
  id: string;
  userId?: string;
  type: "read" | "search" | "source" | "task" | "error" | "performance";
  name: string;
  payload: Record<string, unknown>;
  createdAt: string;
}

export interface ReaderPreference {
  theme: "light" | "dark";
  fontFamily: string;
  fontWeight: number;
  script: "source" | "simplified" | "traditional";
  fontSize: number;
  letterSpacing: number;
  lineHeight: number;
  paragraphSpacing: number;
  textIndent: number;
  textAlign: "start" | "justify";
  textAlignLast: "auto" | "start" | "justify";
  paddingTop: number;
  paddingRight: number;
  paddingBottom: number;
  paddingLeft: number;
  contentWidth: number;
  lightTextColor: string;
  darkTextColor: string;
  lightBackgroundColor: string;
  darkBackgroundColor: string;
  backgroundImageUrl: string;
  autoPage: boolean;
  prefetchChapterCount: number;
}

export interface PreferenceProfile {
  id: string;
  userId: string;
  name: string;
  preference: ReaderPreference;
  isActive: boolean;
}

export interface Book {
  id: string;
  title: string;
  author?: string;
  kind: string;
  description?: string;
  coverUrl?: string;
  tags?: string[];
}

export interface BookSource {
  id: string;
  bookId: string;
  sourceId: string;
  sourceBookId: string;
  latestChapterTitle?: string;
  latestChapterAt?: string;
  chapterCount?: number;
}

export interface SearchResult {
  book: Book;
  sources: BookSource[];
}

export interface SourceBookDetail {
  kind: ContentKind;
  title: string;
  author?: string;
  sourceBookId: string;
  sourceUrl?: string;
  coverUrl?: string;
  description?: string;
  tags?: string[];
  latestChapterTitle?: string;
  latestChapterAt?: string;
  chapterCount?: number;
  chapters?: ChapterRef[];
}

export type SearchStreamEvent =
  | {
      type: "source";
      sourceId: string;
      results: SearchResult[];
    }
  | {
      type: "failure";
      sourceId: string;
      reason: string;
    }
  | {
      type: "done";
    }
  | {
      type: "error";
      reason: string;
    };

export interface BookshelfViewItem {
  item: {
    id: string;
    userId?: string;
    bookId: string;
    kind?: string;
    status: string;
    groupId?: string;
    activeSourceId?: string;
    addedAt?: string;
    updatedAt?: string;
  };
  book: Book;
  sources: BookSource[];
  progress: {
    sourceId?: string;
    chapterId: string;
    chapterIndex: number;
    paragraphIndex?: number;
    charOffset?: number;
    scrollRatio?: number;
    updatedAt?: string;
  } | null;
}

export interface UserBackupSnapshot {
  version: 1;
  exportedAt: string;
  user: UserSpace;
  books: Book[];
  bookSources: BookSource[];
  bookshelf: Array<Record<string, unknown>>;
  progress: Array<Record<string, unknown>>;
  preferences: PreferenceProfile[];
  sourceSettings: Array<Record<string, unknown>>;
  sourceGroups: UserSourceGroup[];
  cachedChapters: Array<Record<string, unknown>>;
}

export interface ContentBlock {
  type: "paragraph" | "heading" | "image" | "divider";
  text?: string;
  src?: string;
  alt?: string;
  width?: number;
  height?: number;
}

export interface OpenChapterResult {
  chapter: {
    title: string;
    chapterId: string;
    blocks: ContentBlock[];
  };
  chapters: Array<{ id: string; title: string; index: number }>;
  prefetchedChapterIds: string[];
  cachedChapterIds?: string[];
  cachedChapters?: Array<{
    title: string;
    chapterId: string;
    blocks: ContentBlock[];
  }>;
}

export interface ChapterRef {
  id: string;
  sourceId: string;
  bookSourceId: string;
  title: string;
  index: number;
}

export interface ReadingProgress {
  id: string;
  userId: string;
  bookId: string;
  sourceId: string;
  chapterId: string;
  chapterIndex: number;
  paragraphIndex?: number;
  charOffset?: number;
  scrollRatio?: number;
  updatedAt: string;
}

export interface AgentSourcePlan {
  targetUrl: string;
  origin: string;
  candidateSourceId: string;
  displayName: string;
  kinds: ContentKind[];
  capabilities: string[];
  requiresLogin: boolean;
  steps: Array<{
    id: string;
    stage: string;
    title: string;
    tool: string;
    purpose: string;
    expectedOutput: string[];
  }>;
  acceptanceChecks: string[];
}

export interface SessionPayload {
  user: UserSpace;
  sources: SourceManifest[];
  activePreference: PreferenceProfile;
  preferences: PreferenceProfile[];
}

export async function login(username: string, password: string): Promise<SessionPayload> {
  return api("/api/auth/login", {
    method: "POST",
    body: { username, password },
  });
}

export async function logout(): Promise<{ ok: boolean }> {
  return api("/api/auth/logout", {
    method: "POST",
  });
}

export async function getSession(): Promise<SessionPayload> {
  return api("/api/session");
}

export async function changeOwnPassword(currentPassword: string, nextPassword: string): Promise<{ ok: true }> {
  return api("/api/auth/password", {
    method: "POST",
    body: { currentPassword, nextPassword },
  });
}

export async function getAdminUsers(): Promise<{ users: UserSpace[]; groups: UserGroup[] }> {
  return api("/api/admin/users");
}

export async function createAdminUser(input: {
  username: string;
  password: string;
  displayName?: string;
  groupIds?: string[];
  sourceIds?: string[];
}): Promise<UserSpace> {
  return api("/api/admin/users", {
    method: "POST",
    body: input,
  });
}

export async function updateAdminUser(
  userId: string,
  patch: Partial<Pick<UserSpace, "displayName" | "role" | "status" | "groupIds" | "sourceIds">>,
): Promise<UserSpace> {
  return api(`/api/admin/users/${encodeURIComponent(userId)}`, {
    method: "PUT",
    body: patch,
  });
}

export async function resetAdminUserPassword(userId: string, password: string): Promise<{ ok: true }> {
  return api(`/api/admin/users/${encodeURIComponent(userId)}/password`, {
    method: "POST",
    body: { password },
  });
}

export async function saveAdminUserGroups(groups: Array<Pick<UserGroup, "id" | "name" | "sourceIds">>): Promise<UserGroup[]> {
  return api("/api/admin/user-groups", {
    method: "PUT",
    body: { groups },
  });
}

export async function updatePreference(profileId: string, preference: ReaderPreference): Promise<PreferenceProfile> {
  return api(`/api/preferences/${encodeURIComponent(profileId)}`, {
    method: "PUT",
    body: {
      preference,
    },
  });
}

export async function setSourceEnabled(userId: string, sourceId: string, enabled: boolean) {
  return api(`/api/sources/${sourceId}/settings`, {
    method: "PUT",
    body: { userId, enabled },
  });
}

export async function getSourceDashboard(
  userId: string,
): Promise<{ sources: SourceManifest[]; enabledSourceIds: string[]; statuses: SourceStatus[] }> {
  return api(`/api/sources?userId=${encodeURIComponent(userId)}`);
}

export async function getSourceGroups(userId: string): Promise<UserSourceGroup[]> {
  return api(`/api/source-groups?userId=${encodeURIComponent(userId)}`);
}

export async function saveSourceGroups(userId: string, groups: Array<Omit<UserSourceGroup, "userId" | "updatedAt">>): Promise<UserSourceGroup[]> {
  return api("/api/source-groups", {
    method: "PUT",
    body: { userId, groups },
  });
}

export async function runSourceCheck(sourceId: string): Promise<{ status: string; result?: { status?: SourceStatus }; error?: string }> {
  return api("/api/tasks/source-check", {
    method: "POST",
    body: { sourceId },
  });
}

export async function getSourceDiscover(sourceId: string, userId: string, targetUrl?: string): Promise<SourceDiscoverResult> {
  const params = new URLSearchParams({ userId });
  if (targetUrl) params.set("targetUrl", targetUrl);

  return api(`/api/sources/${encodeURIComponent(sourceId)}/discover?${params.toString()}`);
}

export async function getTelemetry(userId?: string): Promise<TelemetryEvent[]> {
  return api(userId ? `/api/telemetry?userId=${encodeURIComponent(userId)}` : "/api/telemetry");
}

export async function getSourceBookDetail(sourceId: string, sourceBookId: string): Promise<SourceBookDetail> {
  return api(`/api/sources/${encodeURIComponent(sourceId)}/book-detail?sourceBookId=${encodeURIComponent(sourceBookId)}`);
}

export async function searchBooks(userId: string, keyword: string, kind: ContentKind): Promise<{ results: SearchResult[] }> {
  return api(`/api/search?userId=${encodeURIComponent(userId)}&q=${encodeURIComponent(keyword)}&kind=${kind}`);
}

export async function streamSearchBooks(
  userId: string,
  keyword: string,
  kind: ContentKind,
  onEvent: (event: SearchStreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(`/api/search/stream?userId=${encodeURIComponent(userId)}&q=${encodeURIComponent(keyword)}&kind=${kind}`, {
    signal,
    credentials: "include",
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: "搜索请求失败" }));
    throw new Error(typeof body.error === "string" ? body.error : "搜索请求失败");
  }

  const reader = response.body?.getReader();
  if (!reader) {
    const text = await response.text();
    readSearchStreamLines(text, onEvent, signal);
    return;
  }

  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    if (signal?.aborted) throw createAbortError();
    const { done, value } = await reader.read();
    if (done) break;
    if (signal?.aborted) throw createAbortError();

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";

    readSearchStreamLines(lines.join("\n"), onEvent, signal);
  }

  if (signal?.aborted) throw createAbortError();
  buffer += decoder.decode();
  readSearchStreamLines(buffer, onEvent, signal);
}

export async function addToBookshelf(userId: string, bookId: string, activeSourceId: string, groupId?: string) {
  return api("/api/bookshelf", {
    method: "POST",
    body: {
      userId,
      bookId,
      status: "reading",
      activeSourceId,
      groupId,
    },
  });
}

export async function addSourceBookToBookshelf(
  userId: string,
  sourceId: string,
  sourceBookId: string,
  groupId?: string,
): Promise<BookshelfViewItem> {
  return api("/api/bookshelf/source", {
    method: "POST",
    body: {
      userId,
      sourceId,
      sourceBookId,
      status: "reading",
      groupId,
    },
  });
}

export async function setBookshelfActiveSource(userId: string, bookId: string, activeSourceId: string) {
  return api("/api/bookshelf/active-source", {
    method: "PUT",
    body: {
      userId,
      bookId,
      activeSourceId,
    },
  });
}

export async function setBookshelfGroup(userId: string, bookId: string, groupId: string) {
  return api("/api/bookshelf/group", {
    method: "PUT",
    body: {
      userId,
      bookId,
      groupId,
    },
  });
}

export async function removeFromBookshelf(userId: string, bookId: string): Promise<{ removed: boolean }> {
  return api(`/api/bookshelf?userId=${encodeURIComponent(userId)}&bookId=${encodeURIComponent(bookId)}`, {
    method: "DELETE",
  });
}

export async function getBookshelfView(userId: string, kind: ContentKind): Promise<BookshelfViewItem[]> {
  return api(`/api/bookshelf/view?userId=${encodeURIComponent(userId)}&kind=${kind}`);
}

export async function exportBackup(userId: string): Promise<UserBackupSnapshot> {
  return api(`/api/backup?userId=${encodeURIComponent(userId)}`);
}

export async function importBackup(snapshot: UserBackupSnapshot): Promise<UserSpace> {
  return api("/api/backup/import", {
    method: "POST",
    body: snapshot as unknown as Record<string, unknown>,
  });
}

export async function openChapter(
  userId: string,
  sourceId: string,
  sourceBookId: string,
  chapterId: string,
  prefetchCount?: number,
): Promise<OpenChapterResult> {
  const params = new URLSearchParams({
    userId,
    sourceId,
    sourceBookId,
    chapterId,
  });
  if (prefetchCount !== undefined) params.set("prefetchCount", String(prefetchCount));

  return api(`/api/reader/open?${params.toString()}`);
}

export async function getChapters(userId: string, sourceId: string, sourceBookId: string): Promise<ChapterRef[]> {
  return api(
    `/api/chapters?userId=${encodeURIComponent(userId)}&sourceId=${encodeURIComponent(sourceId)}&sourceBookId=${encodeURIComponent(
      sourceBookId,
    )}`,
  );
}

export async function saveProgress(input: {
  userId: string;
  bookId: string;
  sourceId: string;
  chapterId: string;
  chapterIndex: number;
  paragraphIndex?: number;
  charOffset?: number;
  scrollRatio?: number;
}): Promise<ReadingProgress> {
  return api("/api/progress", {
    method: "PUT",
    body: input,
  });
}

export async function createAgentSourcePlan(input: {
  targetUrl: string;
  sourceId?: string;
  displayName?: string;
  kind: ContentKind;
  requiresLogin: boolean;
}): Promise<AgentSourcePlan> {
  return api("/api/agent/source-plan", {
    method: "POST",
    body: input,
  });
}

async function api(path: string, options: { method?: string; body?: Record<string, unknown> } = {}) {
  const response = await fetch(path, {
    method: options.method ?? "GET",
    body: options.body ? JSON.stringify(options.body) : undefined,
    headers: options.body ? { "Content-Type": "application/json" } : undefined,
    credentials: "include",
  });
  const body = await response.json();

  if (!response.ok) {
    throw new Error(body.error ?? "API 请求失败");
  }

  return body;
}

function readSearchStreamLines(text: string, onEvent: (event: SearchStreamEvent) => void, signal?: AbortSignal): void {
  for (const line of text.split("\n")) {
    if (signal?.aborted) throw createAbortError();
    if (!line.trim()) continue;
    const event = JSON.parse(line) as SearchStreamEvent;
    onEvent(event);
    if (event.type === "error") throw new Error(event.reason);
  }
}

function createAbortError(): Error {
  return new DOMException("搜索已停止", "AbortError");
}
